import React, { useEffect, useState } from 'react';
import { CreditCard, Loader2, Save, Check, Eye, EyeOff, ShieldCheck, AlertTriangle } from 'lucide-react';
import { adminApi } from '../lib/api';

const PaymentSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ public_key: '', secret_key: '', enabled: false, test_mode: false });
  const [showSecret, setShowSecret] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await adminApi.getPaymentSettings();
      setSettings(s);
      setForm({ public_key: s.public_key || '', secret_key: '', enabled: !!s.enabled, test_mode: !!s.test_mode });
    } catch (e) {
      setMessage("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setMessage('');
    try {
      const body = {
        public_key: form.public_key.trim(),
        enabled: !!form.enabled,
        test_mode: !!form.test_mode,
      };
      if (form.secret_key.trim()) body.secret_key = form.secret_key.trim();
      const s = await adminApi.updatePaymentSettings(body);
      setSettings(s);
      setForm({ public_key: s.public_key || '', secret_key: '', enabled: !!s.enabled, test_mode: !!s.test_mode });
      setMessage("Paramètres de paiement enregistrés");
      setTimeout(() => setMessage(''), 3500);
    } catch (e) {
      setMessage("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F4A340]" /></div>;

  return (
    <div className="space-y-4" data-testid="payment-settings">
      <div>
        <h3 className="text-lg font-bold text-slate-900">Paiement · Mobile Money</h3>
        <p className="text-sm text-slate-500">Configurez les clés FedaPay pour accepter les paiements MTN, Moov, Celtiis et carte bancaire</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-[#F4A340]/30">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#F4A340]/20 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-[#E89534]" />
            </div>
            <div>
              <div className="font-bold text-slate-900">FedaPay</div>
              <div className="text-xs text-slate-500">Passerelle de paiement · Bénin, Togo, Côte d'Ivoire, Sénégal</div>
            </div>
          </div>
          {settings?.has_secret_key ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
              <ShieldCheck className="w-3.5 h-3.5" /> Configuré
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
              <AlertTriangle className="w-3.5 h-3.5" /> À configurer
            </span>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Clé publique</label>
          <input
            data-testid="fedapay-public-key-input"
            placeholder="pk_live_xxx ou pk_sandbox_xxx"
            value={form.public_key}
            onChange={(e) => setForm({ ...form, public_key: e.target.value })}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm font-mono"
          />
          <p className="text-xs text-slate-400 mt-1">Utilisée côté client · commence par <code className="bg-slate-100 px-1 rounded">pk_</code></p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Clé secrète</label>
          {settings?.has_secret_key && (
            <div className="mb-2 text-xs text-slate-500 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              Actuellement enregistrée : <code className="bg-slate-100 px-2 py-0.5 rounded font-mono">{settings.secret_key_masked}</code>
            </div>
          )}
          <div className="relative">
            <input
              data-testid="fedapay-secret-key-input"
              type={showSecret ? 'text' : 'password'}
              placeholder={settings?.has_secret_key ? "Laisser vide pour ne pas changer" : "sk_live_xxx ou sk_sandbox_xxx"}
              value={form.secret_key}
              onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
              className="w-full px-4 py-2.5 pr-11 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm font-mono"
            />
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label={showSecret ? 'Masquer' : 'Afficher'}
            >
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-1">⚠️ Ne jamais partager · stockée chiffrée côté serveur · commence par <code className="bg-slate-100 px-1 rounded">sk_</code></p>
        </div>

        <label className="flex items-center gap-3 cursor-pointer p-3 bg-amber-50 border border-amber-200 rounded-xl">
          <input
            data-testid="fedapay-test-mode-toggle"
            type="checkbox"
            checked={form.test_mode}
            onChange={(e) => setForm({ ...form, test_mode: e.target.checked })}
            className="w-5 h-5 accent-amber-500"
          />
          <div>
            <div className="font-semibold text-sm text-amber-900">Mode Sandbox / Test</div>
            <div className="text-xs text-amber-700">
              Utiliser <code className="bg-white px-1 rounded">sandbox-api.fedapay.com</code> avec des clés <code className="bg-white px-1 rounded">pk_sandbox_*</code> et <code className="bg-white px-1 rounded">sk_sandbox_*</code> pour tester sans débiter de vrais comptes
            </div>
          </div>
        </label>

        <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 rounded-xl">
          <input
            data-testid="fedapay-enabled-toggle"
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            className="w-5 h-5 accent-[#F4A340]"
          />
          <div>
            <div className="font-semibold text-sm text-slate-900">Activer le paiement en ligne</div>
            <div className="text-xs text-slate-500">Si désactivé, les clients continueront à payer via WhatsApp / simulateur</div>
          </div>
        </label>

        {message && (
          <div className={`p-3 rounded-xl text-sm flex items-center gap-2 ${message.startsWith('Erreur') ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
            {message.startsWith('Erreur') ? <AlertTriangle className="w-4 h-4" /> : <Check className="w-4 h-4" />} {message}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button
            data-testid="payment-settings-save-btn"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 text-xs text-slate-600 space-y-2">
        <div className="font-bold text-slate-800 text-sm">Comment obtenir vos clés ?</div>
        <ol className="list-decimal pl-4 space-y-1">
          <li>Créez un compte sur <a href="https://fedapay.com" target="_blank" rel="noreferrer" className="text-[#E89534] underline font-semibold">fedapay.com</a></li>
          <li>Dashboard → <strong>Paramètres</strong> → <strong>API</strong></li>
          <li>Copiez la <em>Clé publique</em> et la <em>Clé secrète</em> de production (Live) ou de test (Sandbox)</li>
          <li>Collez-les ci-dessus et activez le paiement</li>
        </ol>
      </div>
    </div>
  );
};

export default PaymentSettings;
